// The dashboard's keyboard cursor: the places it rests (rows, stack heads,
// runs of members outside the section and their members), where a key
// moves it, and where it goes when the rows change under it. Pure: the
// dashboard describes what it shows and runs the moves
// (commands/dashboard.ts reads commands into them).
import type { Folds } from './stacks';

/** What the cursor is asked to do: step by row or by group, fold or unfold, or open what it is on. */
export type CursorCommand =
  /** To the next (1) or previous (-1) row. */
  | { kind: 'move'; delta: 1 | -1 }
  /** To the next (1) or previous (-1) stack or section. */
  | { kind: 'group'; delta: 1 | -1 }
  /** Unfold (`open` true) or fold the stack or run at the cursor, or the one it is in. */
  | { kind: 'fold'; open: boolean }
  /** Open the row's pull request, or toggle the stack or run it heads. */
  | { kind: 'open' };

/** A row of a stack's rail: a pull request (or a member shown alone), or a run of members that collapses. */
export type RailItem = { kind: 'row'; key: string } | { kind: 'run'; key: string; open: boolean; members: string[] };

/** What a section shows, top to bottom: single rows and stacks. Keys are unique on the page. */
export interface SectionLayout {
  items: ({ kind: 'row'; key: string } | { kind: 'stack'; key: string; open: boolean; rows: RailItem[] })[];
}

/** A place the cursor rests. */
export interface Stop {
  key: string;
  /** It starts a section or is a stack's head: where `J` and `K` land. */
  group: boolean;
  /** A stack's head or a run: whether it is unfolded. Null for a row. */
  open: boolean | null;
  /** The stack head or run it is shown inside, which folds it away; null at the top level. */
  parent: string | null;
}

/** The stops of these sections, top to bottom, leaving out rows a folded stack or a collapsed run hides. */
export function dashStops(sections: readonly SectionLayout[]): Stop[] {
  const out: Stop[] = [];
  for (const s of sections)
    s.items.forEach((item, i) => {
      if (item.kind === 'row') {
        out.push({ key: item.key, group: i === 0, open: null, parent: null });
        return;
      }
      out.push({ key: item.key, group: true, open: item.open, parent: null });
      if (!item.open) return;
      for (const r of item.rows) {
        if (r.kind === 'row') {
          out.push({ key: r.key, group: false, open: null, parent: item.key });
          continue;
        }
        out.push({ key: r.key, group: false, open: r.open, parent: item.key });
        if (r.open) for (const m of r.members) out.push({ key: m, group: false, open: null, parent: r.key });
      }
    });
  return out;
}

/** Where a key leaves the cursor, and the stack head or run to fold or unfold (`toggle`), if any. */
export interface Move {
  key: string | null;
  toggle: string | null;
}

/**
 * The cursor after `command` from `cursor`. A move stops at the ends, and
 * starts at the first stop when the cursor is on none. `J` goes to the next
 * stack or section; `K` to the start of the current one, or from there the
 * one before. Folding works on the stack or run at the cursor, or else the
 * one it is inside, the cursor going to its head; unfolding works only on
 * a folded one at the cursor.
 */
export function navigate(stops: readonly Stop[], cursor: string | null, command: Exclude<CursorCommand, { kind: 'open' }>): Move {
  const i = stops.findIndex((s) => s.key === cursor);
  if (i < 0) return { key: stops[0]?.key ?? cursor, toggle: null };
  const at = stops[i];
  const stay = { key: at.key, toggle: null };
  switch (command.kind) {
    case 'move':
      return { key: stops[Math.max(0, Math.min(stops.length - 1, i + command.delta))].key, toggle: null };
    case 'group': {
      const found =
        command.delta > 0 ? stops.slice(i + 1).find((s) => s.group) : stops.slice(0, i).reverse().find((s) => s.group);
      return found ? { key: found.key, toggle: null } : stay;
    }
    case 'fold':
      if (command.open) return at.open === false ? { key: at.key, toggle: at.key } : stay;
      if (at.open) return { key: at.key, toggle: at.key };
      if (at.parent) return { key: at.parent, toggle: at.parent };
      return stay;
  }
}

/**
 * Where the cursor goes when the stops change from `before` to `after`: it
 * stays on its stop while that is shown (wherever it moved); else to the
 * nearest head that folded it away, else the next stop of `before` still
 * shown, else the one before it. A cursor `before` did not show (rows still
 * loading, or one kept from before a reload) is kept, as is any cursor when
 * nothing is shown.
 */
export function settle(before: readonly Stop[], after: readonly Stop[], cursor: string | null): string | null {
  if (cursor === null || !after.length) return cursor;
  const shown = new Set(after.map((s) => s.key));
  if (shown.has(cursor)) return cursor;
  const i = before.findIndex((s) => s.key === cursor);
  if (i < 0) return cursor;
  const byKey = new Map(before.map((s) => [s.key, s]));
  for (let p = before[i].parent; p; p = byKey.get(p)?.parent ?? null) if (shown.has(p)) return p;
  const next = before.slice(i + 1).find((s) => shown.has(s.key)) ?? before.slice(0, i).reverse().find((s) => shown.has(s.key));
  return next?.key ?? after[0].key;
}

/** A short, stable hash of `s` (FNV-1a, 32 bits, base 36). */
function hash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193);
  return (h >>> 0).toString(36);
}

/**
 * A name for each section, from its definition (`sectionKey`), for stop keys
 * and folds: a section keeps it when others are added, removed or moved. A
 * second section of the same definition gets `-2`, and so on.
 */
export function sectionIds(keys: readonly string[]): string[] {
  const seen = new Map<string, number>();
  return keys.map((k) => {
    const id = `s${hash(k)}`;
    const n = (seen.get(id) ?? 0) + 1;
    seen.set(id, n);
    return n === 1 ? id : `${id}-${n}`;
  });
}

/** The dashboard's fold state: folded stacks, and opened runs of members outside the section (by stop). */
export interface FoldState {
  folds: Folds;
  runs: ReadonlySet<string>;
}

/** `state` as text, to keep for the tab (see `decodeFoldState`). */
export function encodeFoldState(state: FoldState): string {
  return JSON.stringify({ folds: state.folds.map((f) => ({ section: f.section, ids: [...f.ids] })), runs: [...state.runs] });
}

const strings = (x: unknown): x is string[] => Array.isArray(x) && x.every((v) => typeof v === 'string');

/** The fold state `encodeFoldState` wrote; nothing folded or opened from anything else. */
export function decodeFoldState(raw: string | null): FoldState {
  const none: FoldState = { folds: [], runs: new Set() };
  if (!raw) return none;
  let x: unknown;
  try {
    x = JSON.parse(raw);
  } catch {
    return none;
  }
  if (typeof x !== 'object' || x === null || Array.isArray(x)) return none;
  const { folds, runs } = x as { folds?: unknown; runs?: unknown };
  if (!Array.isArray(folds) || !strings(runs)) return none;
  const ok = folds.every((f) => typeof f === 'object' && f !== null && typeof f.section === 'string' && strings(f.ids));
  if (!ok) return none;
  return { folds: folds.map((f: { section: string; ids: string[] }) => ({ section: f.section, ids: new Set(f.ids) })), runs: new Set(runs) };
}

/** What a key does at `stop`, the cursor's (see `stopAction`). */
export type StopAction = 'focus' | 'navigate' | 'start' | 'control';

/**
 * What a cursor key does, given whether the focus is on the cursor's stop
 * (`focused`): from elsewhere, any key only brings the focus back to it
 * (`focus`), so none acts on a row out of sight. On a stack's head, `o`
 * opens the stack's start (`start`); Enter there toggles the head as its
 * button does. Elsewhere `o` works the stop's control (`control`); moves
 * and folds `navigate`.
 */
export function stopAction(stop: Stop, command: CursorCommand, focused: boolean): StopAction {
  if (!focused) return 'focus';
  if (command.kind !== 'open') return 'navigate';
  return stop.parent === null && stop.open !== null ? 'start' : 'control';
}
